import { ArrowUpRight, CheckCircle2, Star, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";
import { JsonLd } from "../components/page/JsonLd";
import { SITE_URL, absoluteUrl } from "../lib/site";

export default function BookPage() {
  const p = useSection("page_book", {});
  const settings = useSettings({ amazon_book_url: "", contact_email: "mike@builtdifferenthockey.com" });
  const amazon = p.primary_cta_url || settings.amazon_book_url;
  const learn = p.what_youll_learn || [];

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/book" image={p.image_url} />
      <JsonLd
        id="book-defensemans-guide"
        data={{
          "@context": "https://schema.org",
          "@type": "Book",
          "@id": `${SITE_URL}/book#book`,
          name: p.hero_heading || "A Defenseman's Guide to Elite Play",
          author: {
            "@type": "Person",
            "@id": `${SITE_URL}/coach#mike-fournier`,
            name: "Mike Fournier",
          },
          image: p.image_url ? absoluteUrl(p.image_url) : undefined,
          url: `${SITE_URL}/book`,
          description: p.meta_description || p.intro_body,
          bookFormat: "https://schema.org/Paperback",
          inLanguage: "en-US",
          publisher: { "@id": `${SITE_URL}/#organization` },
          ...(amazon ? { sameAs: amazon, offers: { "@type": "Offer", url: amazon, availability: "https://schema.org/InStock" } } : {}),
        }}
      />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} subheading={p.hero_subheading} />

      <section className="bg-[#0a0a0a] py-20 md:py-28 border-b border-white/5 overflow-hidden">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-x-0 gap-y-10 md:gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="md:col-span-5 flex justify-center">
            {p.image_url && (
              <div className="relative group">
                <div className="absolute -inset-4 bg-brand-light/30 blur-2xl group-hover:bg-brand-light/50 transition-colors duration-500" />
                <div className="relative">
                  <div className="absolute -bottom-4 -right-4 h-full w-full bg-brand" />
                  <img src={resolveUrl(p.image_url)} alt="A Defenseman's Guide to Elite Play — book by Coach Mike Fournier" loading="lazy" decoding="async" className="relative h-[440px] md:h-[520px] w-auto object-contain shadow-2xl" />
                </div>
              </div>
            )}
          </motion.div>
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 overline">
              <BookOpen className="h-4 w-4" /> By Coach Mike Fournier
            </div>
            <p className="mt-6 text-zinc-300 leading-relaxed text-base md:text-lg whitespace-pre-line">{p.intro_body}</p>

            <div className="mt-6 flex items-center gap-2 text-sm text-zinc-400">
              {[...Array(5)].map((_, i) => (<Star key={i} className="h-4 w-4 fill-brand-light text-brand-light" />))}
              <span className="ml-1">For defensemen, parents, and coaches.</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              {amazon && (
                <a href={amazon} target="_blank" rel="noopener noreferrer" className="btn-primary">
                  {p.primary_cta_label || "Buy on Amazon"} <ArrowUpRight className="h-5 w-5" />
                </a>
              )}
              <a href={`mailto:${settings.contact_email}?subject=About%20the%20Book`} className="btn-ghost">
                {p.secondary_cta_label || "Email Coach Mike"}
              </a>
            </div>
          </div>
        </div>
      </section>

      {learn.length > 0 && (
        <section className="bg-[#0f0f0f] py-20 md:py-24 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
            <p className="overline">{p.what_youll_learn_label || "What You'll Learn"}</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight mb-8">The truth about the position.</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {learn.map((w, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.05 }} className="flex items-start gap-3 border-b border-white/10 pb-3 text-zinc-200">
                  <CheckCircle2 className="h-4 w-4 text-brand-light shrink-0 mt-1" />
                  <span>{w}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {p.audience_body && (
        <section className="bg-[#0a0a0a] py-20 md:py-24 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
            <p className="overline">{p.audience_label || "Who It's For"}</p>
            <p className="mt-5 text-lg md:text-xl text-zinc-300 leading-relaxed whitespace-pre-line">{p.audience_body}</p>
          </div>
        </section>
      )}
    </PageShell>
  );
}
